import { v4 as uuidv4 } from 'uuid'
import { BaseOfflineStore } from './base.store'
import { OfflineDocument } from '../types/database.types'
import { Document } from '@/core/models/document.model'

export class DocumentOfflineStore extends BaseOfflineStore<OfflineDocument> {
    constructor() {
        super('documents')
    }

    async createOfflineDocument(data: {
        filename: string
        size: number
        mimeType: string
        base64Data: string
        consultationId: string
        biologicalId?: string
        objectType?: 'consultation' | 'patient' | 'expertise' | 'establishment'
    }): Promise<OfflineDocument> {
        const now = Date.now()
        const document: OfflineDocument = {
            _id: uuidv4(),
            _offlineId: uuidv4(),
            _syncStatus: 'pending',
            _lastModified: now,
            _createdAt: now,
            objectId: data.consultationId,
            _fileData: data.base64Data,
            _fileSize: data.size,
            _fileType: data.mimeType,
            // Document fields - only using properties from Document interface
            key: uuidv4(),
            filename: data.filename,
            objectType: data.objectType || 'consultation' as const,
            url: '', // Will be set when synced
            ...(data.biologicalId && { biologicalId: data.biologicalId })
        }

        const createdDocument = await this.add(document)

        // The logic for adding to the sync queue has been moved.
        // The component that calls this method is now responsible for
        // calling `addToSyncQueue` from the `useSyncContext`.

        return createdDocument
    }

    async getByConsultationId(consultationId: string): Promise<OfflineDocument[]> {
        const documents = await this.getAll()
        return documents.filter(doc => doc.objectId === consultationId)
    }

    async getByBiologicalId(biologicalId: string): Promise<OfflineDocument[]> {
        const documents = await this.getAll()
        return documents.filter(doc => doc.objectId === biologicalId)
    }

    async getByObjectId(objectType: string, objectId: string): Promise<OfflineDocument[]> {
        const allDocuments = await this.getAll()
        return allDocuments.filter(doc => doc.objectType === objectType && doc.objectId === objectId)
    }

    async getByOfflineId(offlineId: string): Promise<OfflineDocument | undefined> {
        try {
            const documents = await this.getByIndex('_offlineId', offlineId)
            return documents[0]
        } catch (error) {
            console.warn('Failed to get document by offlineId, trying alternative method:', error)
            // Fallback: search through all documents
            const allDocuments = await this.getAll()
            return allDocuments.find(doc => doc._offlineId === offlineId)
        }
    }

    async getBySyncStatus(status: 'synced' | 'pending' | 'failed'): Promise<OfflineDocument[]> {
        return this.getByIndex('_syncStatus', status)
    }

    async markAsSynced(document: OfflineDocument, serverId: string, serverUrl: string): Promise<OfflineDocument> {
        console.log(`Marking document as synced: ${document.filename}`, {
            documentId: document._id,
            serverId,
            serverUrl
        })

        const updatedDocument: OfflineDocument = {
            ...document,
            // Don't overwrite _id with the key - keep the original ID for IndexedDB
            // The key is the S3 path/identifier, not the document ID
            key: serverId, // This is actually the document key, not an ID
            _syncStatus: 'synced',
            _lastModified: Date.now(),
            url: serverUrl,
            // Remove file data after successful sync to save space
            _fileData: undefined,
        }

        console.log(`About to update document in store:`, {
            originalId: document._id,
            updatedId: updatedDocument._id,
            syncStatus: updatedDocument._syncStatus
        })

        try {
            const result = await this.update(updatedDocument)
            console.log(`Successfully marked document as synced: ${document.filename}`)
            return result
        } catch (error) {
            console.error(`Failed to mark document as synced: ${document.filename}`, error)
            throw error
        }
    }

    async markAsFailed(document: OfflineDocument, error: string): Promise<OfflineDocument> {
        const updatedDocument: OfflineDocument = {
            ...document,
            _syncStatus: 'failed',
            _lastModified: Date.now(),
        }

        return this.update(updatedDocument)
    }

    async deleteByKey(key: string): Promise<void> {
        const allDocuments = await this.getAll()
        const documentToDelete = allDocuments.find(doc => doc.key === key)
        if (documentToDelete) {
            await this.delete(documentToDelete._id)
        }
    }

    async updateFilename(key: string, newFilename: string): Promise<OfflineDocument | undefined> {
        const allDocuments = await this.getAll()
        const documentToUpdate = allDocuments.find(doc => doc.key === key)

        if (documentToUpdate) {
            const updatedDocument: OfflineDocument = {
                ...documentToUpdate,
                filename: newFilename,
                _lastModified: Date.now(),
                _syncStatus: 'pending', // Mark as pending sync due to update
            }
            return this.update(updatedDocument)
        }

        return undefined
    }

    private async fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                const result = reader.result as string
                // Remove the data URL prefix to get just the base64 data
                resolve(result.split(',')[1])
            }
            reader.onerror = error => reject(error)
        })
    }

    async base64ToBlob(base64Data: string, mimeType: string): Promise<Blob> {
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        return new Blob([byteArray], { type: mimeType })
    }

    async getFileBlob(document: OfflineDocument): Promise<Blob | null> {
        if (!document._fileData || !document._fileType) {
            return null
        }

        return this.base64ToBlob(document._fileData, document._fileType)
    }

    async getFileUrl(document: OfflineDocument): Promise<string | null> {
        const blob = await this.getFileBlob(document)
        if (!blob) {
            return null
        }

        return URL.createObjectURL(blob)
    }
}

// Create a singleton instance
export const documentOfflineStore = new DocumentOfflineStore() 